// src/services/questionCacheService.ts
// 問題データの高速キャッシュサービス（スマホ対応）

import { collection, getDocs, query, orderBy, getDoc, doc, Timestamp } from 'firebase/firestore';
import { db } from '@/firebase';

const DB_NAME = 'chusho-questions-cache';
const DB_VERSION = 6; // バージョンアップして古いキャッシュを無効化（経営法務2024 AI問題のexplanationContentHtml修正対応）
const CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7日間
const CACHE_DATA_VERSION = '6.0'; // キャッシュデータのバージョン

export interface CachedQuestionData {
  id: string;
  question_number: number;
  title: string;
  content: string;
  imageUrl?: string;
  explanation?: string;
  explanationContentHtml?: string;
  answerType: 'select' | 'radio' | 'essay';
  options: string[];
  correctAnswer: string[];
  categories?: string[];
  globalAccuracy?: number;
  stats?: {
    totalAnswers: number;
    correctAnswers: number;
  };
  similarQuestions?: { id: string; year: string }[];
  characterLimit?: number;
}

export interface CachedAIQuestion extends CachedQuestionData {
  parentId: string;
}

export interface CachedYearData {
  subjectId: string;
  year: string;
  questions: CachedQuestionData[];
  aiQuestions: Record<string, CachedAIQuestion[]>; // parentId -> AI questions
  similarQuestions: Record<string, CachedQuestionData[]>; // parentId -> similar questions
  cachedAt: number;
  version: string;
  dataHash?: string; // データの整合性チェック用
}

let dbPromise: Promise<IDBDatabase> | null = null;

const getDB = (): Promise<IDBDatabase> => {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // 年度データストア
      if (!db.objectStoreNames.contains('yearData')) {
        const store = db.createObjectStore('yearData', { keyPath: 'id' });
        store.createIndex('bySubject', 'subjectId', { unique: false });
        store.createIndex('byCachedAt', 'cachedAt', { unique: false });
      }

      // メタデータストア
      if (!db.objectStoreNames.contains('metadata')) {
        db.createObjectStore('metadata', { keyPath: 'key' });
      }
    };
  });

  return dbPromise;
};

export class QuestionCacheService {
  /**
   * 年度の全問題データをキャッシュから取得
   */
  static async getCachedYearData(
    subjectId: string,
    year: string
  ): Promise<CachedYearData | null> {
    try {
      const db = await getDB();
      return new Promise((resolve, reject) => {
        const tx = db.transaction('yearData', 'readonly');
        const store = tx.objectStore('yearData');
        const id = `${subjectId}_${year}`;
        const request = store.get(id);

        request.onerror = () => reject(request.error);
        request.onsuccess = () => {
          const result = request.result as CachedYearData | undefined;

          // キャッシュの有効期限とバージョンチェック
          if (result &&
              Date.now() - result.cachedAt < CACHE_DURATION &&
              result.version === CACHE_DATA_VERSION) {
            console.log(`📦 Cache HIT: ${subjectId} ${year} (v${result.version})`);
            console.log(`   - AI問題数（最初の問題）: ${result.aiQuestions[Object.keys(result.aiQuestions)[0]]?.length || 0}`);
            resolve(result);
          } else {
            if (result) {
              if (result.version !== CACHE_DATA_VERSION) {
                console.log(`🔄 Cache VERSION MISMATCH: ${subjectId} ${year} (cached: v${result.version}, expected: v${CACHE_DATA_VERSION}) - キャッシュを破棄します`);
              } else {
                console.log(`⏰ Cache EXPIRED: ${subjectId} ${year}`);
              }
            } else {
              console.log(`❌ Cache MISS: ${subjectId} ${year}`);
            }
            resolve(null);
          }
        };
      });
    } catch (e) {
      console.error('Failed to get cached year data:', e);
      return null;
    }
  }

  /**
   * 年度の全問題データをキャッシュに保存
   */
  static async cacheYearData(data: CachedYearData): Promise<void> {
    try {
      const db = await getDB();
      return new Promise((resolve, reject) => {
        const tx = db.transaction('yearData', 'readwrite');
        const store = tx.objectStore('yearData');
        const id = `${data.subjectId}_${data.year}`;

        const dataToCache = {
          ...data,
          id,
          cachedAt: Date.now(),
          version: CACHE_DATA_VERSION,
          dataHash: this.generateDataHash(data),
        };

        const request = store.put(dataToCache);

        request.onerror = () => reject(request.error);
        request.onsuccess = () => {
          console.log(`💾 Cached: ${data.subjectId} ${data.year}`);
          resolve();
        };
      });
    } catch (e) {
      console.error('Failed to cache year data:', e);
    }
  }

  /**
   * Firestoreから年度データを取得してキャッシュ
   */
  static async fetchAndCacheYearData(
    appId: string,
    subjectId: string,
    year: string
  ): Promise<CachedYearData> {
    console.log(`🔍 Fetching from Firestore: ${subjectId} ${year}`);

    const basePath = `apps/${appId}/exam_questions/${subjectId}/years/${subjectId}_${year}/questions`;
    const questionsRef = collection(db, basePath);
    const questionsSnap = await getDocs(query(questionsRef, orderBy('question_number')));

    const questions: CachedQuestionData[] = [];
    const aiQuestions: Record<string, CachedAIQuestion[]> = {};
    const similarQuestions: Record<string, CachedQuestionData[]> = {};

    // 全問題を並列取得
    await Promise.all(
      questionsSnap.docs.map(async (qDoc) => {
        const data = qDoc.data();

        // 問題データの構築
        const questionData: CachedQuestionData = {
          id: qDoc.id,
          question_number: data.question_number || 0,
          title: data.title || '',
          content: data.content || data.question_text || '',
          imageUrl: data.imageUrl || '',
          explanation: data.explanation || '',
          explanationContentHtml: data.explanationContentHtml || '',
          answerType: data.answerType || 'radio',
          options: data.options || [],
          correctAnswer: Array.isArray(data.correctAnswer)
            ? data.correctAnswer.map((x: any) => String(x))
            : [String(data.correctAnswer)],
          categories: data.categories || [],
          globalAccuracy: data.globalAccuracy || data.global_accuracy,
          stats: data.stats,
          similarQuestions: data.similarQuestions,
          characterLimit: data.characterLimit,
        };

        questions.push(questionData);

        // AI問題の取得
        try {
          const aiCol = collection(db, `${basePath}/${qDoc.id}/aiQuestions`);
          const aiSnap = await getDocs(aiCol);
          const aiQs: CachedAIQuestion[] = aiSnap.docs.map((aiDoc) => {
            const aiData = aiDoc.data();
            return {
              id: aiDoc.id,
              parentId: qDoc.id,
              question_number: aiData.question_number || 999,
              title: aiData.title || '',
              content: aiData.question_text || aiData.content || '',
              imageUrl: aiData.imageUrl || '',
              explanation: aiData.explanation || '',
              explanationContentHtml: aiData.explanationContentHtml || '',
              answerType: aiData.answerType || 'radio',
              options: aiData.options || [],
              correctAnswer: Array.isArray(aiData.correctAnswer)
                ? aiData.correctAnswer.map((x: any) => String(x))
                : [String(aiData.correctAnswer)],
              categories: aiData.categories || [],
              globalAccuracy: aiData.globalAccuracy,
              stats: aiData.stats,
            };
          });
          if (aiQs.length > 0) {
            aiQuestions[qDoc.id] = aiQs.sort((a, b) => a.question_number - b.question_number);
          }
        } catch (e) {
          console.error(`Failed to fetch AI questions for ${qDoc.id}:`, e);
        }

        // 類似問題の取得（必要に応じて実装）
        // similarQuestions[qDoc.id] = ...
      })
    );

    const yearData: CachedYearData = {
      subjectId,
      year,
      questions: questions.sort((a, b) => a.question_number - b.question_number),
      aiQuestions,
      similarQuestions,
      cachedAt: Date.now(),
      version: CACHE_DATA_VERSION,
    };

    // キャッシュに保存
    await this.cacheYearData(yearData);

    return yearData;
  }

  /**
   * 差分チェック: FirestoreのAI問題数とキャッシュを比較
   */
  static async checkForUpdates(
    appId: string,
    subjectId: string,
    year: string,
    cached: CachedYearData
  ): Promise<boolean> {
    try {
      const basePath = `apps/${appId}/exam_questions/${subjectId}/years/${subjectId}_${year}/questions`;
      const questionsRef = collection(db, basePath);
      const questionsSnap = await getDocs(query(questionsRef, orderBy('question_number')));

      // 問題数が異なる場合は更新が必要
      if (questionsSnap.size !== cached.questions.length) {
        console.log(`📊 問題数の差分検出: Firestore=${questionsSnap.size}, Cache=${cached.questions.length}`);
        return true;
      }

      // 全ての問題のAI問題数をチェック（より確実に差分を検出）
      console.log('🔍 全問題のAI問題数をチェック中...');
      for (const qDoc of questionsSnap.docs) {
        const aiCol = collection(db, `${basePath}/${qDoc.id}/aiQuestions`);
        const aiSnap = await getDocs(aiCol);
        const cachedAICount = cached.aiQuestions[qDoc.id]?.length || 0;

        if (aiSnap.size !== cachedAICount) {
          console.log(`📊 AI問題数の差分検出 (問題ID: ${qDoc.id}): Firestore=${aiSnap.size}, Cache=${cachedAICount}`);
          return true;
        }
      }

      console.log('✅ 差分なし - すべての問題が一致');
      return false;
    } catch (e) {
      console.error('差分チェックエラー:', e);
      return false; // エラー時はキャッシュを使用
    }
  }

  /**
   * キャッシュ優先で年度データを取得
   * キャッシュがない場合はFirestoreから取得してキャッシュ
   * 差分がある場合は自動的に再読み込み
   */
  static async getYearData(
    appId: string,
    subjectId: string,
    year: string,
    forceRefresh: boolean = false
  ): Promise<CachedYearData> {
    // 強制リフレッシュの場合はキャッシュをスキップ
    if (forceRefresh) {
      console.log('🔄 強制リフレッシュ');
      return await this.fetchAndCacheYearData(appId, subjectId, year);
    }

    const cached = await this.getCachedYearData(subjectId, year);

    if (cached) {
      // キャッシュがある場合、差分をチェック
      console.log('🔍 差分チェック開始...');
      const hasUpdates = await this.checkForUpdates(appId, subjectId, year, cached);

      if (hasUpdates) {
        console.log('🔄 差分検出により自動更新');
        return await this.fetchAndCacheYearData(appId, subjectId, year);
      }

      console.log('✅ キャッシュ使用（差分なし）');
      return cached;
    }

    // キャッシュがない場合はFirestoreから取得
    console.log('📥 初回取得');
    return await this.fetchAndCacheYearData(appId, subjectId, year);
  }

  /**
   * 特定の問題のみを更新（差分更新）
   */
  static async updateSingleQuestion(
    subjectId: string,
    year: string,
    questionId: string,
    updatedData: Partial<CachedQuestionData>
  ): Promise<void> {
    try {
      const cached = await this.getCachedYearData(subjectId, year);
      if (!cached) return;

      // 問題を更新
      const questionIndex = cached.questions.findIndex(q => q.id === questionId);
      if (questionIndex !== -1) {
        cached.questions[questionIndex] = {
          ...cached.questions[questionIndex],
          ...updatedData,
        };
        await this.cacheYearData(cached);
        console.log(`🔄 Updated question in cache: ${questionId}`);
      }
    } catch (e) {
      console.error('Failed to update single question:', e);
    }
  }

  /**
   * データハッシュの生成（簡易版）
   */
  private static generateDataHash(data: CachedYearData): string {
    const str = JSON.stringify({
      questionCount: data.questions.length,
      aiCount: Object.keys(data.aiQuestions).length,
      firstQuestionId: data.questions[0]?.id,
    });
    return btoa(str);
  }

  /**
   * 古いキャッシュをクリア（30日以上前のもの）
   */
  static async clearOldCache(): Promise<void> {
    try {
      const db = await getDB();
      const cutoffTime = Date.now() - 30 * 24 * 60 * 60 * 1000;

      return new Promise((resolve, reject) => {
        const tx = db.transaction('yearData', 'readwrite');
        const store = tx.objectStore('yearData');
        const index = store.index('byCachedAt');
        const request = index.openCursor(IDBKeyRange.upperBound(cutoffTime));

        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest).result;
          if (cursor) {
            cursor.delete();
            cursor.continue();
          }
        };

        tx.oncomplete = () => {
          console.log('🧹 Cleared old cache');
          resolve();
        };
        tx.onerror = () => reject(tx.error);
      });
    } catch (e) {
      console.error('Failed to clear old cache:', e);
    }
  }

  /**
   * 特定の科目・年度のキャッシュを削除
   */
  static async clearYearCache(subjectId: string, year: string): Promise<void> {
    try {
      const db = await getDB();
      return new Promise((resolve, reject) => {
        const tx = db.transaction('yearData', 'readwrite');
        const store = tx.objectStore('yearData');
        const cacheKey = `${subjectId}_${year}`;
        const request = store.delete(cacheKey);

        request.onerror = () => reject(request.error);
        request.onsuccess = () => {
          console.log(`🗑️ Cleared cache: ${cacheKey}`);
          resolve();
        };
      });
    } catch (e) {
      console.error('Failed to clear year cache:', e);
    }
  }

  /**
   * 全キャッシュをクリア
   */
  static async clearAllCache(): Promise<void> {
    try {
      const db = await getDB();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(['yearData', 'metadata'], 'readwrite');
        tx.objectStore('yearData').clear();
        tx.objectStore('metadata').clear();
        tx.oncomplete = () => {
          console.log('🗑️ Cleared all question cache');
          resolve();
        };
        tx.onerror = () => reject(tx.error);
      });
    } catch (e) {
      console.error('Failed to clear all cache:', e);
    }
  }

  /**
   * キャッシュサイズを取得
   */
  static async getCacheSize(): Promise<{ count: number; estimatedMB: number }> {
    try {
      const db = await getDB();
      return new Promise((resolve, reject) => {
        const tx = db.transaction('yearData', 'readonly');
        const store = tx.objectStore('yearData');
        const countRequest = store.count();

        countRequest.onerror = () => reject(countRequest.error);
        countRequest.onsuccess = () => {
          const count = countRequest.result;
          // 1年度あたり平均1MBと仮定
          const estimatedMB = Math.round(count * 1);
          resolve({ count, estimatedMB });
        };
      });
    } catch (e) {
      console.error('Failed to get cache size:', e);
      return { count: 0, estimatedMB: 0 };
    }
  }
}
